import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../lib/cn';

// Grouped sections for the admin sidebar. Sections are
// categories of related admin work; rendering uses
// <details> for native expand/collapse (no state
// management, keyboard-accessible, persistent across
// page reloads via the browser's own state). Default-
// open the section that contains the active route so
// the user lands with the relevant items visible.
type AdminLink = { to: string; label: string; end?: boolean };
type AdminSection = {
  id: string;
  label: string;
  // Short description shown when the section is
  // collapsed. Helps admins remember what's in each
  // group without expanding it; the description is
  // hidden when the section is open so it doesn't add
  // visual noise.
  blurb?: string;
  links: AdminLink[];
};
const adminSections: AdminSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    blurb: 'Health, audit, and tool launches',
    links: [
      { to: '/admin', label: 'Dashboard', end: true },
      { to: '/admin/audit', label: 'Audit log' },
      { to: '/admin/condition-tester', label: 'Condition tester' },
    ],
  },
  {
    id: 'people',
    label: 'People & teams',
    blurb: 'Manage users, teams, and team activity',
    // v0.7.23: Team activity moved here from the XP
    // section. It's a per-team view of who's done
    // what recently, which is more of a "who" surface
    // than a "scoring" surface. Grouping it under People
    // & teams matches how operators mentally model the
    // menu (team activity = a view into people, not
    // into the scoring engine).
    links: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/teams', label: 'Teams' },
      { to: '/admin/team-activity', label: 'Team activity' },
      // v0.8.9: Commander — operational overview of who's
      // working right now across the team. Click in to see
      // recent moves + counters for the chosen window
      // (1h / 8h / 24h / 7d).
      { to: '/admin/commander', label: 'Commander' },
      // v0.8.9: campaign health — the flow analytics view. Where the
      // Commander answers "who is working right now", this answers "is
      // the campaign flowing, and what will not land in time".
      { to: '/admin/campaign-health', label: 'Campaign health' },
      // Pull requests in against out. Its own page: a PR carries no
      // commitment, so "behind" means backlog size here, not a promise.
      { to: '/admin/pr-flow', label: 'PR flow' },
      // v0.8.9: the sprint as a battlefield. Operators only — it is a
      // view of the active campaign, not a player surface.
      { to: '/admin/battlemap', label: 'Battlemap' },
    ],
  },
  {
    id: 'xp',
    label: 'XP & competencies',
    blurb: 'The progression model',
    links: [
      { to: '/admin/competencies', label: 'Competencies' },
      { to: '/admin/scoring-rules', label: 'Scoring rules' },
      { to: '/admin/badges', label: 'Badges' },
      { to: '/admin/heatmap', label: 'Skill heatmap' },
    ],
  },
  {
    id: 'activities',
    label: 'Activities & content',
    blurb: 'Quests, daily quests, and the public surface',
    links: [
      { to: '/admin/quests', label: 'Quests' },
      { to: '/admin/adventures', label: 'Adventures' },
      { to: '/admin/bosses', label: 'Boss Fights' },
      { to: '/admin/rewards', label: 'Rewards' },
      { to: '/admin/chronicles', label: 'Chronicles' },
      { to: '/admin/help', label: 'Help & Setup' },
      { to: '/admin/daily-quests', label: 'Daily quest templates' },
      { to: '/admin/event-types', label: 'Event types' },
      { to: '/admin/public-board', label: 'Public board' },
    ],
  },
  {
    id: 'ingestors',
    label: 'Ingestors',
    blurb: 'Connect external sources',
    links: [
      { to: '/admin/ingestors', label: 'Ingestors' },
    ],
  },
];

export function AdminShell() {
  const location = useLocation();
  // Open the section containing the active route by
  // default. Computed once per render via
  // useMemo'd key so the <details> open attribute
  // doesn't fight user toggles within the same
  // navigation.
  const activeSectionId = (() => {
    const path = location.pathname;
    for (const section of adminSections) {
      if (section.links.some((l) => (l.end ? path === l.to : path.startsWith(l.to)))) {
        return section.id;
      }
    }
    return null;
  })();
  return (
    <div className="grid gap-6 md:grid-cols-[14rem,1fr]">
      <aside className="md:sticky md:top-4 md:self-start">
        <nav className="card flex flex-row gap-1 overflow-x-auto p-2 md:flex-col md:gap-3 md:overflow-visible md:p-2">
          {adminSections.map((section) => (
            // Native <details>; using the open attribute
            // to default-open the active section. The
            // user can collapse/expand manually; the
            // browser persists that choice across
            // reloads (vs. our stateless React version,
            // which would reset every navigation).
            <details
              key={section.id}
              // The `group` class lets the blurb's
              // `group-open:hidden` modifier pick up the
              // open state on this element without any JS.
              className="group md:[&[open]>summary]:mb-1"
              open={section.id === activeSectionId}
            >
              <summary
                className={cn(
                  'flex cursor-pointer select-none items-center justify-between rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wide',
                  section.id === activeSectionId
                    ? 'text-ink'
                    : 'text-ink-muted hover:text-ink',
                )}
              >
                <span>{section.label}</span>
                {/* Caret that rotates on open. Pure CSS via
                    the [open] selector and a transform on
                    the marker, so no JS. Visually shows
                    the user the section is expandable. */}
                <span
                  className="ml-2 text-ink-dim transition-transform group-open:rotate-90"
                  aria-hidden
                >
                  ▶
                </span>
              </summary>
              {/* The blurb gives context when the section
                  is collapsed. When the section is open,
                  the items speak for themselves; hide the
                  blurb to save vertical space. The blurb
                  uses the `group-open:hidden` modifier
                  (and the <details> element above carries
                  the `group` class) so the same <p> works
                  in both states without a JS branch. */}
              {section.blurb ? (
                <p className="hidden px-2 pb-1 text-[10px] text-ink-dim group-open:hidden md:block">
                  {section.blurb}
                </p>
              ) : null}
              <ul className="mt-1 flex flex-row gap-1 overflow-x-auto md:flex-col md:gap-0 md:overflow-visible">
                {section.links.map((l) => (
                  <li key={l.to}>
                    <NavLink
                      to={l.to}
                      end={l.end}
                      className={({ isActive }) =>
                        cn(
                          'block rounded-md px-3 py-1.5 text-sm whitespace-nowrap',
                          isActive
                            ? 'bg-bg-elevated text-ink'
                            : 'text-ink-muted hover:bg-bg-elevated/60 hover:text-ink',
                        )
                      }
                    >
                      {l.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </nav>
      </aside>
      <div>
        <Outlet />
      </div>
    </div>
  );
}
